import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FolderKanban, Users, ListTree, Send, Inbox, Settings, LogOut, Building2,
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/subcontractors", label: "Subcontractors", icon: Users },
  { to: "/csi-codes", label: "CSI Codes", icon: ListTree },
  { to: "/send-itb", label: "Send ITB", icon: Send },
  { to: "/inbox", label: "Inbox Routing", icon: Inbox },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-60 shrink-0 border-r border-border bg-card flex flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="leading-tight">
            <p className="font-display font-black tracking-tight text-lg">Bid Router</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground -mt-0.5">by Conduit Systems</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-2 pb-2">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.company_name}</p>
          </div>
          <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={doLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
    </div>
  );
}

export function PageHeader({ title, description, actions }) {
  return (
    <header className="h-auto min-h-16 sticky top-0 z-50 bg-card border-b border-border px-8 py-4 flex items-center justify-between gap-4">
      <div>
        <h1 className="font-display font-black tracking-tight text-2xl">{title}</h1>
        {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </header>
  );
}
